(function () {
  'use strict';

  /* Site-wide flowing-ink layer — GPU stable-fluids sim on one fixed,
     full-viewport canvas. Ink is scroll-locked to the page content.
     Color contrast is automatic: a per-section luminance map picks
     luminous gold glow over dark sections and deep pigment over light
     ones, and rendered alpha is hard-capped so text stays readable.
     Degrades gracefully: no WebGL or prefers-reduced-motion → no-op. */

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // ink palettes — chosen at injection time from the section under the splat
  var GLOW = [          // over dark sections (luminous)
    [0.910, 0.659, 0.090], // maktar gold
    [1.000, 0.840, 0.420], // warm glow
    [0.290, 0.620, 0.540]  // muted teal
  ];
  var PIGMENT = [       // over light sections (deep ink)
    [0.102, 0.102, 0.180], // navy #1A1A2E
    [0.770, 0.557, 0.055], // dark gold #C48E0E
    [0.184, 0.275, 0.408]  // indigo
  ];
  var INK_WEIGHTS = [0, 0, 0, 1, 1, 2];

  var config = {
    SIM_RESOLUTION: 128,
    DYE_RESOLUTION: 512,
    PRESSURE_ITERATIONS: 16,
    VELOCITY_DISSIPATION: 0.7,
    DYE_DISSIPATION: 0.25,
    SPLAT_RADIUS: 0.0042,
    SPLAT_FORCE: 1600,
    ALPHA_STRENGTH: 1.3,   // how fast ink amount → opacity
    ALPHA_CAP: 0.5,        // hard ceiling — keeps text contrast safe
    POINTER_BASE: 0.016,
    POINTER_GAIN: 0.4,
    POINTER_MAX: 0.05,
    AGENT_AMOUNT: 0.005
  };

  // ---- fixed overlay canvas ----
  var canvas = document.createElement('canvas');
  canvas.className = 'ink-layer';
  canvas.setAttribute('aria-hidden', 'true');
  var cs = canvas.style;
  cs.position = 'fixed'; cs.top = 0; cs.left = 0;
  cs.width = '100vw'; cs.height = '100vh';
  cs.pointerEvents = 'none';
  cs.zIndex = '4'; // above section content, below ribbons(5)/nav(90)/widgets
  document.body.appendChild(canvas);

  var gl, ext;
  function initGL() {
    var p = { alpha: true, depth: false, stencil: false, antialias: false, preserveDrawingBuffer: false };
    gl = canvas.getContext('webgl2', p);
    var isGL2 = !!gl;
    if (!gl) gl = canvas.getContext('webgl', p) || canvas.getContext('experimental-webgl', p);
    if (!gl) return false;
    var halfFloat, supportLinear, fRGBA, fRG, fR;
    if (isGL2) {
      gl.getExtension('EXT_color_buffer_float');
      supportLinear = true;
      halfFloat = gl.HALF_FLOAT;
      fRGBA = pickFormat(gl.RGBA16F, gl.RGBA, halfFloat);
      fRG = pickFormat(gl.RG16F, gl.RG, halfFloat);
      fR = pickFormat(gl.R16F, gl.RED, halfFloat);
    } else {
      var hf = gl.getExtension('OES_texture_half_float');
      supportLinear = !!gl.getExtension('OES_texture_half_float_linear');
      halfFloat = hf ? hf.HALF_FLOAT_OES : null;
      if (!halfFloat) return false;
      fRGBA = pickFormat(gl.RGBA, gl.RGBA, halfFloat);
      fRG = fRGBA; fR = fRGBA;
    }
    if (!fRGBA) return false;
    ext = { halfFloat: halfFloat, linear: supportLinear, fRGBA: fRGBA, fRG: fRG, fR: fR };
    return true;
  }

  function pickFormat(internalFormat, format, type) {
    if (!renderable(internalFormat, format, type)) {
      switch (internalFormat) {
        case gl.R16F: return pickFormat(gl.RG16F, gl.RG, type);
        case gl.RG16F: return pickFormat(gl.RGBA16F, gl.RGBA, type);
        default: return null;
      }
    }
    return { internalFormat: internalFormat, format: format };
  }

  function renderable(internalFormat, format, type) {
    var t = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, 4, 4, 0, format, type, null);
    var f = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, f);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, t, 0);
    var ok = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
    gl.deleteFramebuffer(f); gl.deleteTexture(t);
    return ok;
  }

  if (!initGL()) { canvas.remove(); return; }

  // ---- section luminance map (page coords → dark/light) ----
  var sections = [];
  var bodyDark = false;
  function bgLuma(el) {
    while (el) {
      var c = getComputedStyle(el).backgroundColor;
      var m = c && c.match(/rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)(?:,\s*([\d.]+))?\)/);
      if (m && (m[4] === undefined || parseFloat(m[4]) > 0.1)) {
        return (0.2126 * m[1] + 0.7152 * m[2] + 0.0722 * m[3]) / 255;
      }
      el = el.parentElement;
    }
    return 1;
  }
  function buildLumaMap() {
    var list = [];
    var sy = window.scrollY || window.pageYOffset || 0;
    var els = document.querySelectorAll('section, footer');
    for (var i = 0; i < els.length; i++) {
      var r = els[i].getBoundingClientRect();
      if (r.height < 40) continue;
      list.push({ top: r.top + sy, bottom: r.bottom + sy, dark: bgLuma(els[i]) < 0.5 });
    }
    sections = list;
    bodyDark = bgLuma(document.body) < 0.5;
  }
  function darkAt(clientYpx) {
    var pageY = clientYpx + (window.scrollY || window.pageYOffset || 0);
    for (var i = 0; i < sections.length; i++) {
      if (pageY >= sections[i].top && pageY < sections[i].bottom) return sections[i].dark;
    }
    return bodyDark;
  }
  // palette pick for a splat at normalized sim coords (y up)
  function inkAt(ny, idx) {
    var clientY = (1 - ny) * window.innerHeight;
    return (darkAt(clientY) ? GLOW : PIGMENT)[idx];
  }
  buildLumaMap();
  window.addEventListener('load', buildLumaMap);
  window.addEventListener('resize', function () { setTimeout(buildLumaMap, 200); });

  // ---- shaders ----
  var VS = 'precision highp float;attribute vec2 aPosition;varying vec2 vUv,vL,vR,vT,vB;uniform vec2 texelSize;void main(){vUv=aPosition*0.5+0.5;vL=vUv-vec2(texelSize.x,0.);vR=vUv+vec2(texelSize.x,0.);vT=vUv+vec2(0.,texelSize.y);vB=vUv-vec2(0.,texelSize.y);gl_Position=vec4(aPosition,0.,1.);}';

  // vec4 splat: dye stores (color*amount) in rgb, amount in a
  var FS_SPLAT = 'precision highp float;precision highp sampler2D;varying vec2 vUv;uniform sampler2D uTarget;uniform float aspectRatio;uniform vec4 color;uniform vec2 point;uniform float radius;uniform float uClamp;void main(){vec2 p=vUv-point.xy;p.x*=aspectRatio;vec4 splat=exp(-dot(p,p)/radius)*color;vec4 base=texture2D(uTarget,vUv);gl_FragColor=min(base+splat,vec4(uClamp));}';

  // advection with scroll shift (uShift) so ink sticks to page content
  function fsAdvection(manual) {
    return 'precision highp float;precision highp sampler2D;varying vec2 vUv;uniform sampler2D uVelocity;uniform sampler2D uSource;uniform vec2 texelSize;uniform vec2 dyeTexelSize;uniform vec2 uShift;uniform float dt;uniform float dissipation;'
      + (manual
        ? 'vec4 bilerp(sampler2D sam,vec2 uv,vec2 ts){vec2 st=uv/ts-0.5;vec2 iuv=floor(st);vec2 fuv=fract(st);vec4 a=texture2D(sam,(iuv+vec2(0.5,0.5))*ts);vec4 b=texture2D(sam,(iuv+vec2(1.5,0.5))*ts);vec4 c=texture2D(sam,(iuv+vec2(0.5,1.5))*ts);vec4 d=texture2D(sam,(iuv+vec2(1.5,1.5))*ts);return mix(mix(a,b,fuv.x),mix(c,d,fuv.x),fuv.y);}void main(){vec2 coord=vUv-uShift-dt*bilerp(uVelocity,vUv,texelSize).xy*texelSize;vec4 result=bilerp(uSource,coord,dyeTexelSize);'
        : 'void main(){vec2 coord=vUv-uShift-dt*texture2D(uVelocity,vUv).xy*texelSize;vec4 result=texture2D(uSource,coord);')
      + 'if(coord.y<0.0||coord.y>1.0){result=vec4(0.0);}float decay=1.0+dissipation*dt;gl_FragColor=result/decay;}';
  }

  var FS_DIVERGENCE = 'precision mediump float;precision mediump sampler2D;varying vec2 vUv,vL,vR,vT,vB;uniform sampler2D uVelocity;void main(){float L=texture2D(uVelocity,vL).x;float R=texture2D(uVelocity,vR).x;float T=texture2D(uVelocity,vT).y;float B=texture2D(uVelocity,vB).y;vec2 C=texture2D(uVelocity,vUv).xy;if(vL.x<0.0){L=-C.x;}if(vR.x>1.0){R=-C.x;}if(vT.y>1.0){T=-C.y;}if(vB.y<0.0){B=-C.y;}gl_FragColor=vec4(0.5*(R-L+T-B),0.0,0.0,1.0);}';

  var FS_PRESSURE = 'precision mediump float;precision mediump sampler2D;varying vec2 vUv,vL,vR,vT,vB;uniform sampler2D uPressure;uniform sampler2D uDivergence;void main(){float L=texture2D(uPressure,vL).x;float R=texture2D(uPressure,vR).x;float T=texture2D(uPressure,vT).x;float B=texture2D(uPressure,vB).x;float divergence=texture2D(uDivergence,vUv).x;gl_FragColor=vec4((L+R+B+T-divergence)*0.25,0.0,0.0,1.0);}';

  var FS_GRADIENT = 'precision mediump float;precision mediump sampler2D;varying vec2 vUv,vL,vR,vT,vB;uniform sampler2D uPressure;uniform sampler2D uVelocity;void main(){float L=texture2D(uPressure,vL).x;float R=texture2D(uPressure,vR).x;float T=texture2D(uPressure,vT).x;float B=texture2D(uPressure,vB).x;vec2 velocity=texture2D(uVelocity,vUv).xy;velocity.xy-=vec2(R-L,T-B);gl_FragColor=vec4(velocity,0.0,1.0);}';

  // premultiplied-alpha output; alpha hard-capped for text contrast
  var FS_DISPLAY = 'precision highp float;precision highp sampler2D;varying vec2 vUv;uniform sampler2D uDye;uniform float uStrength;uniform float uCap;void main(){vec4 d=texture2D(uDye,vUv);float amt=d.a;float alpha=(1.0-exp(-amt*uStrength))*uCap;vec3 col=d.rgb/max(amt,0.0001);gl_FragColor=vec4(col*alpha,alpha);}';

  function compile(type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
    return s;
  }
  function Program(fsSrc) {
    this.program = gl.createProgram();
    gl.attachShader(this.program, compile(gl.VERTEX_SHADER, VS));
    gl.attachShader(this.program, compile(gl.FRAGMENT_SHADER, fsSrc));
    gl.linkProgram(this.program);
    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(this.program));
    this.uniforms = {};
    var n = gl.getProgramParameter(this.program, gl.ACTIVE_UNIFORMS);
    for (var i = 0; i < n; i++) {
      var name = gl.getActiveUniform(this.program, i).name;
      this.uniforms[name] = gl.getUniformLocation(this.program, name);
    }
  }
  Program.prototype.bind = function () { gl.useProgram(this.program); };

  var manual = !ext.linear;
  var progSplat = new Program(FS_SPLAT);
  var progAdvect = new Program(fsAdvection(manual));
  var progDiv = new Program(FS_DIVERGENCE);
  var progPressure = new Program(FS_PRESSURE);
  var progGradient = new Program(FS_GRADIENT);
  var progDisplay = new Program(FS_DISPLAY);

  var blit = (function () {
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, -1, 1, 1, 1, 1, -1]), gl.STATIC_DRAW);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array([0, 1, 2, 0, 2, 3]), gl.STATIC_DRAW);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(0);
    return function (target) {
      if (target == null) {
        gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      } else {
        gl.viewport(0, 0, target.width, target.height);
        gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
      }
      gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
    };
  })();

  function createFBO(w, h, intF, fmt, type, filter) {
    var texture = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, intF, w, h, 0, fmt, type, null);
    var fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    gl.viewport(0, 0, w, h);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    return {
      texture: texture, fbo: fbo, width: w, height: h,
      texelSizeX: 1 / w, texelSizeY: 1 / h,
      attach: function (id) { gl.activeTexture(gl.TEXTURE0 + id); gl.bindTexture(gl.TEXTURE_2D, texture); return id; }
    };
  }
  function createDoubleFBO(w, h, intF, fmt, type, filter) {
    var a = createFBO(w, h, intF, fmt, type, filter);
    var b = createFBO(w, h, intF, fmt, type, filter);
    return {
      width: w, height: h, texelSizeX: 1 / w, texelSizeY: 1 / h,
      get read() { return a; }, get write() { return b; },
      swap: function () { var t = a; a = b; b = t; }
    };
  }
  function getResolution(base) {
    var aspect = gl.drawingBufferWidth / gl.drawingBufferHeight;
    if (aspect < 1) aspect = 1 / aspect;
    var min = Math.round(base), max = Math.round(base * aspect);
    return gl.drawingBufferWidth > gl.drawingBufferHeight ? { width: max, height: min } : { width: min, height: max };
  }

  var dye, velocity, divergence, pressure;
  function initFramebuffers() {
    var simRes = getResolution(config.SIM_RESOLUTION);
    var dyeRes = getResolution(config.DYE_RESOLUTION);
    var filter = ext.linear ? gl.LINEAR : gl.NEAREST;
    gl.disable(gl.BLEND);
    dye = createDoubleFBO(dyeRes.width, dyeRes.height, ext.fRGBA.internalFormat, ext.fRGBA.format, ext.halfFloat, filter);
    velocity = createDoubleFBO(simRes.width, simRes.height, ext.fRG.internalFormat, ext.fRG.format, ext.halfFloat, filter);
    divergence = createFBO(simRes.width, simRes.height, ext.fR.internalFormat, ext.fR.format, ext.halfFloat, gl.NEAREST);
    pressure = createDoubleFBO(simRes.width, simRes.height, ext.fR.internalFormat, ext.fR.format, ext.halfFloat, gl.NEAREST);
  }
  function resizeCanvas() {
    var dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    var w = Math.max(1, Math.floor(window.innerWidth * dpr));
    var h = Math.max(1, Math.floor(window.innerHeight * dpr));
    if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; return true; }
    return false;
  }
  resizeCanvas();
  initFramebuffers();

  function correctRadius(r) {
    var aspect = canvas.width / canvas.height;
    return aspect > 1 ? r * aspect : r;
  }
  function splat(x, y, dx, dy, ink, amount, radiusScale) {
    var radius = correctRadius(config.SPLAT_RADIUS * (radiusScale || 1));
    progSplat.bind();
    gl.uniform1i(progSplat.uniforms.uTarget, velocity.read.attach(0));
    gl.uniform1f(progSplat.uniforms.aspectRatio, canvas.width / canvas.height);
    gl.uniform2f(progSplat.uniforms.point, x, y);
    gl.uniform4f(progSplat.uniforms.color, dx, dy, 0, 0);
    gl.uniform1f(progSplat.uniforms.radius, radius);
    gl.uniform1f(progSplat.uniforms.uClamp, 10000.0);
    gl.uniform2f(progSplat.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    blit(velocity.write); velocity.swap();
    gl.uniform1i(progSplat.uniforms.uTarget, dye.read.attach(0));
    gl.uniform4f(progSplat.uniforms.color, ink[0] * amount, ink[1] * amount, ink[2] * amount, amount);
    gl.uniform1f(progSplat.uniforms.uClamp, 1.5);
    gl.uniform2f(progSplat.uniforms.texelSize, dye.texelSizeX, dye.texelSizeY);
    blit(dye.write); dye.swap();
  }

  // ---- pointer: ink follows the cursor anywhere on the page ----
  var px = -1, py = -1, pInk = 0;
  window.addEventListener('pointermove', function (e) {
    var nx = e.clientX / window.innerWidth;
    var ny = 1 - e.clientY / window.innerHeight;
    if (px >= 0) {
      var dx = nx - px, dy = ny - py;
      var speed = Math.sqrt(dx * dx + dy * dy);
      if (speed > 0.0005 && speed < 0.3) {
        splat(nx, ny, dx * config.SPLAT_FORCE, dy * config.SPLAT_FORCE,
          inkAt(ny, pInk),
          Math.min(config.POINTER_BASE + speed * config.POINTER_GAIN, config.POINTER_MAX), 1.0);
      }
    }
    px = nx; py = ny;
  }, { passive: true });
  window.addEventListener('pointerout', function (e) {
    if (!e.relatedTarget) {
      px = -1; py = -1;
      pInk = INK_WEIGHTS[Math.floor(Math.random() * INK_WEIGHTS.length)];
    }
  }, { passive: true });

  // ---- ambient drift agents ----
  function Agent() { this.reset(true); }
  Agent.prototype.reset = function (anywhere) {
    this.x = anywhere ? 0.15 + Math.random() * 0.7 : (Math.random() < 0.5 ? 0.08 : 0.92);
    this.y = 0.2 + Math.random() * 0.6;
    this.theta = Math.random() * Math.PI * 2;
    this.ink = INK_WEIGHTS[Math.floor(Math.random() * INK_WEIGHTS.length)];
    this.life = 4 + Math.random() * 6;
    this.pause = 1 + Math.random() * 4;
    this.speed = 0.015 + Math.random() * 0.025;
  };
  Agent.prototype.step = function (dt) {
    if (this.pause > 0) { this.pause -= dt; return; }
    this.life -= dt;
    if (this.life <= 0 || this.x < 0.03 || this.x > 0.97 || this.y < 0.05 || this.y > 0.95) { this.reset(false); return; }
    this.theta += (Math.random() - 0.5) * 4.2 * dt;
    var vx = Math.cos(this.theta) * this.speed;
    var vy = Math.sin(this.theta) * this.speed;
    this.x += vx * dt * 6.0;
    this.y += vy * dt * 6.0;
    splat(this.x, this.y, vx * 90, vy * 90, inkAt(this.y, this.ink), config.AGENT_AMOUNT, 1.3);
  };
  var agents = [new Agent(), new Agent()];

  // ---- simulation ----
  var lastScrollY = window.scrollY || 0;
  function clearFields() {
    [dye.read, dye.write, velocity.read, velocity.write].forEach(function (f) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, f.fbo);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
    });
  }

  function step(dt, shiftV) {
    gl.disable(gl.BLEND);
    progAdvect.bind();
    gl.uniform2f(progAdvect.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    if (manual) gl.uniform2f(progAdvect.uniforms.dyeTexelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform2f(progAdvect.uniforms.uShift, 0, shiftV);
    gl.uniform1i(progAdvect.uniforms.uVelocity, velocity.read.attach(0));
    gl.uniform1i(progAdvect.uniforms.uSource, velocity.read.attach(0));
    gl.uniform1f(progAdvect.uniforms.dt, dt);
    gl.uniform1f(progAdvect.uniforms.dissipation, config.VELOCITY_DISSIPATION);
    blit(velocity.write); velocity.swap();

    if (manual) gl.uniform2f(progAdvect.uniforms.dyeTexelSize, dye.texelSizeX, dye.texelSizeY);
    gl.uniform1i(progAdvect.uniforms.uVelocity, velocity.read.attach(0));
    gl.uniform1i(progAdvect.uniforms.uSource, dye.read.attach(1));
    gl.uniform1f(progAdvect.uniforms.dissipation, config.DYE_DISSIPATION);
    blit(dye.write); dye.swap();

    progDiv.bind();
    gl.uniform2f(progDiv.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(progDiv.uniforms.uVelocity, velocity.read.attach(0));
    blit(divergence);

    progPressure.bind();
    gl.uniform2f(progPressure.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(progPressure.uniforms.uDivergence, divergence.attach(0));
    for (var i = 0; i < config.PRESSURE_ITERATIONS; i++) {
      gl.uniform1i(progPressure.uniforms.uPressure, pressure.read.attach(1));
      blit(pressure.write); pressure.swap();
    }

    progGradient.bind();
    gl.uniform2f(progGradient.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(progGradient.uniforms.uPressure, pressure.read.attach(0));
    gl.uniform1i(progGradient.uniforms.uVelocity, velocity.read.attach(1));
    blit(velocity.write); velocity.swap();
  }

  function render() {
    progDisplay.bind();
    gl.uniform2f(progDisplay.uniforms.texelSize, 1 / gl.drawingBufferWidth, 1 / gl.drawingBufferHeight);
    gl.uniform1i(progDisplay.uniforms.uDye, dye.read.attach(0));
    gl.uniform1f(progDisplay.uniforms.uStrength, config.ALPHA_STRENGTH);
    gl.uniform1f(progDisplay.uniforms.uCap, config.ALPHA_CAP);
    blit(null);
  }

  var lastTime = 0, frameCount = 0;
  function frame(now) {
    if (!lastTime) lastTime = now;
    var dt = Math.min((now - lastTime) / 1000, 0.0166);
    lastTime = now;
    if (resizeCanvas()) initFramebuffers();

    // scroll-lock the ink to page content
    var sy = window.scrollY || 0;
    var shiftV = (sy - lastScrollY) / window.innerHeight;
    lastScrollY = sy;
    if (Math.abs(shiftV) > 0.5) { clearFields(); shiftV = 0; } // huge jump → reset

    for (var i = 0; i < agents.length; i++) agents[i].step(dt);
    if (dt > 0 || shiftV !== 0) step(Math.max(dt, 0.001), shiftV);
    render();

    if (++frameCount % 300 === 0) buildLumaMap(); // layout can shift
    requestAnimationFrame(frame);
  }

  // opening blooms
  splat(0.7, 0.6, -20, -10, inkAt(0.6, 0), 0.14, 4.0);
  splat(0.28, 0.4, 15, 12, inkAt(0.4, 1), 0.10, 3.2);

  requestAnimationFrame(frame);
})();
